import { RETURN_TEMPLATE_VARIABLES } from "@babackapp/return-variables-registry";
import type { VariableDefinition } from "@babackapp/variables-registry";
import { useCarriersSuspenseQuery } from "@babackapp/private-api-client/client/use-carriers-query";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";

/**
 * Return variable set adapted to the shared `VariableDefinition` shape, for use
 * outside of React (e.g. passed to `NotificationTemplatePreview` for the return
 * list preview). Runtime-compatible; see `useReturnEditorVariables`.
 */
export const RETURN_PREVIEW_VARIABLES =
  RETURN_TEMPLATE_VARIABLES as unknown as VariableDefinition[];

/**
 * Return-domain variable list for the editor, adapted to the shared
 * `VariableDefinition` shape consumed by the editor core / picker.
 *
 * `ReturnVariableDefinition` is structurally compatible at runtime (same id /
 * name / description / type / group / exampleValue / editable fields); only the
 * literal union types of `group`/`requires`/`availableIn` differ, hence the
 * cast. Shop-dependent example values (e.g. shop.name) are injected here, where
 * the current shop is available.
 */
export function useReturnEditorVariables(): VariableDefinition[] {
  const { data: shop } = useCurrentShopSuspense();
  // Suspense so the carriers are resolved before the editor mounts: the
  // VariablesDictionaryProvider captures its definitions once (useState), so a
  // late-arriving carrier list would leave `return.carrierCode` stuck on the
  // registry fallback and the shipping-instructions preview on the wrong
  // carrier variant (BAB — preview differed between list navigation and a hard
  // refresh of the editor page).
  const { data: carriers } = useCarriersSuspenseQuery(shop.id);
  const shopParams: Record<string, string> = { "shop.name": shop.name };

  // The merchant's configured carriers become the dropdown choices for the
  // `return.carrierCode` variable in the left editor (preview by carrier).
  // Dedupe by code — a merchant can have several entries sharing the same code.
  const seenCodes = new Set<string>();
  const carrierOptions = (carriers ?? []).reduce<
    { value: string; label: string }[]
  >((acc, carrier) => {
    if (!seenCodes.has(carrier.code)) {
      seenCodes.add(carrier.code);
      acc.push({ value: carrier.code, label: carrier.code });
    }
    return acc;
  }, []);

  return RETURN_TEMPLATE_VARIABLES.map((definition) => {
    if (definition.id === "return.carrierCode") {
      // Editable as a dropdown in the left panel, but hidden from the `@` picker
      // (a raw carrier code is a preview control, not text to insert).
      // Default to the merchant's first carrier so a value is pre-selected.
      return {
        ...definition,
        options: carrierOptions,
        pickable: false,
        exampleValue: carrierOptions[0]?.value ?? definition.exampleValue,
      };
    }
    // Timezone of the request: seeded for the "Request date" block preview, but
    // hidden from the `@` picker (it feeds the block, not free text).
    if (definition.id === "return.requestTimezone") {
      return { ...definition, pickable: false };
    }
    if (definition.id in shopParams) {
      return { ...definition, exampleValue: shopParams[definition.id] };
    }
    return definition;
  }) as unknown as VariableDefinition[];
}
